/**
 * logoPlacement - where a newly uploaded bank logo lands on the check.
 *
 * The logo is the check's bank header: it anchors where the bank block
 * itself sits (top-left) and is fitted INTO the header band - the strip
 * between the bank block's top and the account-holder block below it.
 * The previous default dropped it at 50,50 fitted to 220x85, which
 * spilled out of that band and printed across the account holder name
 * and toward the payee line. The write-ups block (logo + bank name /
 * address text) extends right from the mark, like a real bank header,
 * so the band fit is width-generous but height-strict.
 */

/**
 * Whether a placed logo REPLACES the built-in bank header text block
 * (the printed bank name + address lines). A logo carrying its own
 * write-ups (bank name / address / phone beside the mark) IS the
 * header: printing the built-in header too would mix two bank
 * identities on one check (the uploaded USAA block over "Bank of
 * Omni"). A logo-only mark has no write-ups, so it complements the
 * built-in header and the header stays. Derived purely from the
 * existing logo state (logo present + include-write-ups), so saves,
 * restores and the working state keep it consistent with no extra
 * stored flag.
 */
export function logoSuppressesBankHeader(logoPresent: boolean, includeWriteups: boolean): boolean {
    return logoPresent && includeWriteups;
}

export interface LogoZone {
    /** Anchor: the bank block's own left/top for the current layout. */
    left: number;
    top: number;
    /** Widest the placed logo may be (write-ups extend right). */
    maxWidth: number;
    /** Tallest the placed logo may be (the header band height). */
    maxHeight: number;
}

export interface LogoPlacement {
    left: number;
    top: number;
    scale: number;
    width: number;
    height: number;
}

/** Rightward room for a header block before the right-hand date stack. */
export const LOGO_ZONE_MAX_WIDTH = 340;
/** Breathing room kept between the logo's bottom edge and the block below. */
export const LOGO_ZONE_BOTTOM_GAP = 6;
/** Never let a degenerate layout band crush the logo below this height. */
export const LOGO_ZONE_MIN_HEIGHT = 30;

/**
 * Build the header zone for a layout: anchored on the bank block,
 * height = the band between the bank block and the account holder.
 */
export function logoZoneForLayout(bankDetails: { left: number; top: number }, accHolder: { top: number }): LogoZone {
    return {
        left: bankDetails.left,
        top: bankDetails.top,
        maxWidth: LOGO_ZONE_MAX_WIDTH,
        maxHeight: Math.max(LOGO_ZONE_MIN_HEIGHT, accHolder.top - bankDetails.top - LOGO_ZONE_BOTTOM_GAP)
    };
}

/**
 * Fit a logo of natural size (natW x natH) into the header zone,
 * preserving aspect. Returns the anchor and the uniform scale fabric
 * should use. Degenerate natural sizes fall back to scale 1 at the
 * anchor rather than dividing by zero or dropping the logo.
 */
/**
 * Clamp a SAVED logo scale so the placed logo can never cover the
 * account-holder block (Phase 2 live trace: a tall mark restored
 * with a scale saved for a different crop rendered down over the
 * holder name). The rule honours the user's placement — position is
 * never moved and the scale is only reduced, only while the logo
 * sits in the header band: a logo whose top starts at or below the
 * holder's top was deliberately placed elsewhere on the check and
 * is left exactly as saved.
 */
export function clampLogoScaleToZone(natH: number, scale: number, top: number, zone: LogoZone): number {
    if (!natH || natH <= 0 || !scale || scale <= 0) return scale;
    const bandBottom = zone.top + zone.maxHeight;
    if (top >= bandBottom + LOGO_ZONE_BOTTOM_GAP) return scale; // placed below the band on purpose
    const allowed = (bandBottom - top) / natH;
    if (!(allowed > 0)) return scale;
    return Math.min(scale, allowed);
}

export function defaultLogoPlacement(natW: number, natH: number, zone: LogoZone): LogoPlacement {
    if (!natW || !natH || natW <= 0 || natH <= 0) {
        return { left: zone.left, top: zone.top, scale: 1, width: natW || 0, height: natH || 0 };
    }
    const scale = Math.min(zone.maxWidth / natW, zone.maxHeight / natH);
    return {
        left: zone.left,
        top: zone.top,
        scale,
        width: natW * scale,
        height: natH * scale
    };
}
